import type { ChangeEvent } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { SWReactIcons } from '@similarweb/icons';
import { colorsPalettes } from '@similarweb/styles';

import { Button } from '../../../../components/Button';
import { Tooltip } from '../../../../components/Tooltip';
import { useSearchCompetitorsQuery } from '../../../../features/competitors/useSearchCompetitorsQuery';
import type { CraftEmailCompetitor } from '../../../../features/craft-email-agent';
import { useDebouncedValue } from '../../../../hooks/useDebouncedValue';
import { getFaviconUrl } from '../../../../services/favicon';
import { StyledPickerBackArrow, StyledPickerHeader, StyledPickerHeaderTitle } from '../styles';
import { useAiOutreachTracking } from '../useAiOutreachTracking';

import {
  StyledCompetitorApplyWrap,
  StyledCompetitorIcon,
  StyledCompetitorItem,
  StyledCompetitorLabel,
  StyledCompetitorList,
  StyledCompetitorName,
  StyledCompetitorPickerOverlay,
  StyledCompetitorRemoveButton,
  StyledCompetitorRemoveWrap,
  StyledCompetitorSearchWrapper,
  StyledCompetitorSuggestionIcon,
  StyledCompetitorSuggestionItem,
  StyledCompetitorSuggestions,
} from './styles';

const MAX_COMPETITORS = 5;

type AIOutreachCompetitorPickerProps = {
  competitors: CraftEmailCompetitor[];
  prospectDomain: string;
  onApply: (competitors: CraftEmailCompetitor[]) => void;
  onBack: () => void;
};

export const AIOutreachCompetitorPicker = ({
  competitors,
  prospectDomain,
  onApply,
  onBack,
}: AIOutreachCompetitorPickerProps) => {
  const { t } = useTranslation();
  const tracking = useAiOutreachTracking();
  const filteredCompetitors = useMemo(
    () => competitors.filter((c) => c.domain !== prospectDomain),
    [competitors, prospectDomain]
  );
  const [localCompetitors, setLocalCompetitors] = useState<CraftEmailCompetitor[]>(filteredCompetitors);
  const [searchTerm, setSearchTerm] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchWrapperRef = useRef<HTMLDivElement>(null);
  const debouncedSearchTerm = useDebouncedValue(searchTerm, 300);
  const searchQuery = useSearchCompetitorsQuery(debouncedSearchTerm);
  const isMaxReached = localCompetitors.length >= MAX_COMPETITORS;

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (searchWrapperRef.current && !searchWrapperRef.current.contains(e.target as Node)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedDomains = useMemo(() => new Set(localCompetitors.map((c) => c.domain)), [localCompetitors]);

  const isDirty = useMemo(() => {
    const originalDomains = filteredCompetitors.map((c) => c.domain).sort();
    const currentDomains = localCompetitors.map((c) => c.domain).sort();
    return JSON.stringify(originalDomains) !== JSON.stringify(currentDomains);
  }, [filteredCompetitors, localCompetitors]);

  const suggestions = useMemo(() => {
    const results = searchQuery.data ?? [];
    return results.filter(({ name }) => name !== prospectDomain && !selectedDomains.has(name));
  }, [searchQuery.data, selectedDomains, prospectDomain]);

  const isSuggestionsVisible = showSuggestions && searchTerm.length > 0 && suggestions.length > 0;

  const handleSearchChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setShowSuggestions(true);
  }, []);

  const handleRemove = (domain: string) => {
    tracking.trackRemoveCompetitor(domain);
    setLocalCompetitors((prev) => prev.filter((c) => c.domain !== domain));
  };

  const handleAdd = useCallback(
    (result: { name: string; companyName?: string }) => {
      if (localCompetitors.length >= MAX_COMPETITORS) {
        return;
      }
      tracking.trackAddCompetitor(result.name);
      const newCompetitor: CraftEmailCompetitor = {
        domain: result.name,
        companyName: result.companyName ?? result.name,
        favicon: getFaviconUrl(result.name),
      };
      setLocalCompetitors((prev) => [...prev, newCompetitor]);
      setSearchTerm('');
      setShowSuggestions(false);
    },
    [localCompetitors.length, tracking]
  );

  return (
    <StyledCompetitorPickerOverlay data-automation="AIOutreachCompetitorPicker">
      <StyledPickerHeader>
        <StyledPickerBackArrow onClick={onBack}>
          <SWReactIcons iconName="arrow-left" size="xs" iconColor={colorsPalettes.carbon[500]} />
        </StyledPickerBackArrow>
        <StyledPickerHeaderTitle>
          {t('company.overview.ai-outreach.replace_competitors', 'Replace competitors')}
        </StyledPickerHeaderTitle>
      </StyledPickerHeader>
      <StyledCompetitorLabel>
        {t('company.overview.ai-outreach.modify_competitors', 'Modify competitors')}
      </StyledCompetitorLabel>
      <StyledCompetitorSearchWrapper ref={searchWrapperRef}>
        <SWReactIcons iconName="search" size="xs" />
        <input
          type="text"
          value={searchTerm}
          onChange={handleSearchChange}
          onFocus={() => setShowSuggestions(true)}
          placeholder={String(
            t('company.overview.ai-outreach.search_competitors', 'Search for competitors ({{count}}/{{max}})', {
              count: localCompetitors.length,
              max: MAX_COMPETITORS,
            })
          )}
          autoComplete="off"
          disabled={isMaxReached}
        />
        <StyledCompetitorSuggestions isVisible={isSuggestionsVisible}>
          {suggestions.map((result) => (
            <StyledCompetitorSuggestionItem key={result.name} onClick={() => handleAdd(result)}>
              <StyledCompetitorSuggestionIcon>
                <img src={getFaviconUrl(result.name, 14)} width="14" height="14" alt="" />
              </StyledCompetitorSuggestionIcon>
              {result.companyName ?? result.name}
            </StyledCompetitorSuggestionItem>
          ))}
        </StyledCompetitorSuggestions>
      </StyledCompetitorSearchWrapper>
      <StyledCompetitorList>
        {localCompetitors.map((competitor) => (
          <StyledCompetitorItem key={competitor.domain}>
            <StyledCompetitorIcon>
              <img src={getFaviconUrl(competitor.domain, 14)} width="14" height="14" alt="" />
            </StyledCompetitorIcon>
            <StyledCompetitorName>{competitor.companyName || competitor.domain}</StyledCompetitorName>
            <StyledCompetitorRemoveWrap>
              <Tooltip content={String(t('common.remove', 'Remove'))} placement="top-left">
                <StyledCompetitorRemoveButton
                  variant="ghostCarbon300"
                  iconName="delete"
                  iconSize="xs"
                  onClick={() => handleRemove(competitor.domain)}
                />
              </Tooltip>
            </StyledCompetitorRemoveWrap>
          </StyledCompetitorItem>
        ))}
      </StyledCompetitorList>
      <StyledCompetitorApplyWrap>
        <Button
          variant="primary"
          onClick={() => onApply(localCompetitors)}
          disabled={!isDirty || localCompetitors.length === 0}
          data-automation="AIOutreachApplyCompetitors"
        >
          {t('common.apply_changes', 'Apply changes')}
        </Button>
      </StyledCompetitorApplyWrap>
    </StyledCompetitorPickerOverlay>
  );
};
